import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import AdminButton from '../components/AdminButton';
import useAdminButton from '../hooks/useAdminButton';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import useGetGuests from '../api/useGetGuests';
import useWithdrawGuestConsent from '../api/useWithdrawGuestConsent';
import useExportMarketingContacts from '../api/useExportMarketingContacts';
import CustomModal from '../components/modals/CustomModal';
import AdminPage from '../layout/AdminPage';

// Lower case and without accents, so "bela" finds "Béla".
const normalize = (text) => (text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const matches = (guest, query) =>
  normalize(guest.name).includes(query) || normalize(guest.email).includes(query);

const GuestRow = ({ guest, onWithdraw }) => (
  <Paper variant="outlined" data-testid="guest-row" sx={{ p: 2, borderRadius: 2 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
      <Stack spacing={0.25} minWidth={0} flex={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography fontWeight={700} sx={{ color: 'text.primary' }}>{guest.name}</Typography>
          {guest.isBlocked && <Chip label="Letiltva" size="small" color="error" variant="outlined" />}
        </Stack>
        <Typography variant="body2" sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}>
          {guest.email}
          {guest.phoneNumber && ` · ${guest.phoneNumber}`}
        </Typography>
        <Typography variant="body2" sx={{ color: guest.marketingConsentDate ? 'success.main' : 'text.disabled' }}>
          {guest.marketingConsentDate
            ? `Feliratkozva: ${dayjs(guest.marketingConsentDate).format('YYYY. MM. DD.')}`
            : 'Nem iratkozott fel'}
        </Typography>
      </Stack>
      {guest.marketingConsentDate && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => onWithdraw(guest)}
          data-testid="guest-withdraw-consent"
          sx={{ textTransform: 'none', flexShrink: 0 }}
        >
          Leiratkoztatás
        </Button>
      )}
    </Stack>
  </Paper>
);

/**
 * The tenant's guests: search by name or email, withdraw a guest's marketing consent when they
 * asked for it by email, and download the CSV of those who consented. Every guest is listed,
 * blocked ones too; the CSV leaves the blocked ones out.
 */
const GuestsPage = () => {
  const { filledSx } = useAdminButton();
  const { data: guests = [], isLoading } = useGetGuests();
  const { withdrawConsent, isLoading: isWithdrawing } = useWithdrawGuestConsent();
  const { exportMarketingContacts, loading: isExporting } = useExportMarketingContacts();
  const showSnackbar = useSnackbar();
  const [search, setSearch] = useState('');
  const [guestToWithdraw, setGuestToWithdraw] = useState(null);

  const query = normalize(search.trim());
  const shownGuests = useMemo(
    () => (query ? guests.filter((guest) => matches(guest, query)) : guests),
    [guests, query]);
  const subscriberCount = guests.filter((guest) => guest.marketingConsentDate).length;

  const confirmWithdraw = () => {
    withdrawConsent(guestToWithdraw.userId, () => showSnackbar('Leiratkoztatva', 'success'));
    setGuestToWithdraw(null);
  };

  return (
    <AdminPage
      title="Vendégeim"
      subtitle="A regisztrált vendégeid, és hogy kérnek-e marketing levelet. Aki válaszlevélben leiratkozik, azt itt tudod leiratkoztatni."
    >
      <Stack spacing={2.5}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                size="small"
                placeholder="Keresés név vagy email alapján"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                inputProps={{ 'data-testid': 'guest-search' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={exportMarketingContacts}
                disabled={isExporting}
                data-testid="marketing-export"
                disableElevation
                sx={{ textTransform: 'none', flexShrink: 0, ...filledSx }}
              >
                Feliratkozók letöltése (CSV)
              </Button>
            </Stack>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 14 } }}>
              Küldés előtt mindig friss listát tölts le, mert közben lehet, hogy valaki leiratkozott.
              A címeket csak Titkos másolatba (Bcc) tedd, különben mindenki látja a többiekét.
            </Alert>
          </Stack>
        </Paper>

        <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="guest-count">
          {guests.length} vendég, ebből {subscriberCount} feliratkozott
          {query && ` · ${shownGuests.length} találat`}
        </Typography>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        )}

        {!isLoading && shownGuests.length === 0 && (
          <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
            {query ? 'Nincs ilyen vendég.' : 'Még nincs regisztrált vendéged.'}
          </Typography>
        )}

        <Stack spacing={1.5}>
          {shownGuests.map((guest) => (
            <GuestRow key={guest.userId} guest={guest} onWithdraw={setGuestToWithdraw} />
          ))}
        </Stack>
      </Stack>

      <CustomModal
        open={Boolean(guestToWithdraw)}
        onClose={() => setGuestToWithdraw(null)}
        title="Leiratkoztatás"
      >
        <Typography variant="body2" align="center">
          {guestToWithdraw?.email} többé nem kap marketing levelet. Visszakapcsolni csak a vendég tudja,
          a profiljában.
        </Typography>
        <AdminButton text="Leiratkoztatás" onClick={confirmWithdraw} isLoading={isWithdrawing} />
      </CustomModal>
    </AdminPage>
  );
};

export default GuestsPage;
